import React from 'react'
import globalNotification from 'live/services/global-notification'
import i18n from 'live/services/i18n'
import { MarkersData } from '../api'
import ServiceMarker, { Widget } from './service'
import _ from 'lodash'
import Highcharts from 'highcharts'

type ClickMarker = (widgetId: number, marker: MarkersData, position: Record<string, unknown>) => void

export default class VisualMarkers {
    private saveMarker: (marker: MarkersData) => Promise<MarkersData | void>
    private removeMarker: (marker: MarkersData) => Promise<Error | void>
    private insertScheduledTimeoutId: Record<number, number | null> = {}
    private ServiceMarker: ServiceMarker

    constructor(
        saveMarker: (marker: MarkersData) => Promise<MarkersData | void>,
        removeMarker: (marker: MarkersData) => Promise<Error | void>,
        openForm: ClickMarker
    ) {
        this.saveMarker = saveMarker
        this.removeMarker = removeMarker
        this.cancelMarker = _.debounce(this.cancelMarker.bind(this), 100)
        this.ServiceMarker = new ServiceMarker(openForm)
    }

    private possibleMarkerInteraction(evt: MouseEvent & { targetMarker?: MarkersData }, widget: Widget): void {
        const commandClick = evt.altKey || evt.shiftKey
        const marker = evt.targetMarker
        const isOn = this.ServiceMarker.isOn()

        if (commandClick && !isOn && !marker) {
            this.ServiceMarker.addDraftMarker(evt.shiftKey ? widget?.chart?.spanType : widget)

            globalNotification.success(<div>{i18n('marker_widget_action_msg_2')}</div>, false, true, 'sm', {
                className: 'alert-sm success marker medium'
            })
        } else if (commandClick && !isOn && marker) {
            this.ServiceMarker.uncommitDraftMarker(marker)
        } else if (commandClick && isOn) {
            // premature leaving no - action
            this.ServiceMarker.removeDraftMarker()
            globalNotification.hide()
        } else if (isOn) {
            const markers = this.ServiceMarker.commitDraftMarker()

            globalNotification.hide()

            if (markers.length) {
                this.saveMarker(union(markers)).then((annotation: MarkersData | void) => {
                    if (annotation) this.ServiceMarker.updateMarker(annotation)
                })
            }
        }
    }

    turnMarkerModeOn(widget: Widget | string): void {
        this.ServiceMarker.addDraftMarker(widget)
    }

    insert = (event: MarkersData): void => {
        this.ServiceMarker.insertMarker(event)
    }

    remove = (marker: MarkersData): void => {
        this.ServiceMarker.removeMarker(marker)
    }

    clearScheduledMarkers = (widgetId: number, markerId?: string): void => {
        this.ServiceMarker.clearScheduledMarkers(widgetId, markerId)
    }

    private cancelMarker(evt: KeyboardEvent): void {
        const isOn = this.ServiceMarker.isOn()
        const isPressedEscape = evt?.key === 'Escape'

        if (isPressedEscape && isOn) {
            this.ServiceMarker.removeDraftMarker()
            globalNotification.hide()
        }
    }

    private addSeriesListener = (evt: any): void => {
        // set timeout for our fn to be called back after series are added (not before)
        // required when placing labels
        const id = evt.target?.widgetId
        if (this.insertScheduledTimeoutId[id]) window.clearTimeout(this.insertScheduledTimeoutId[id])

        this.insertScheduledTimeoutId[id] = window.setTimeout(() => {
            this.ServiceMarker.insertScheduledMarkers(id)
            this.insertScheduledTimeoutId[id] = null
        }, 500)
    }

    assemble(widget: Widget): () => void {
        const cb = (evt: MouseEvent): void => this.possibleMarkerInteraction(evt, widget)

        widget.el.addEventListener('click', cb)
        window.document.addEventListener('keyup', this.cancelMarker)
        Highcharts.addEvent(widget.chart, 'addSeries', this.addSeriesListener)

        return (): void => {
            widget.el.removeEventListener('click', cb)
            window.document.removeEventListener('keyup', this.cancelMarker)

            if (widget.chart) Highcharts.removeEvent(widget.chart, 'addSeries', this.addSeriesListener)

            this.ServiceMarker.clearScheduledMarkers(widget.options.id)
        }
    }
}

// @TODO: Fix this, will break when you are able to edit the curves position inside the widget.
function union(markers: MarkersData[]): MarkersData {
    const widgets = new Map<number, MarkersData['widgets'][0]>()

    for (const marker of markers) {
        marker.widgets.forEach(widget => widgets.set(widget.widgetId, widget))
    }

    markers[0].widgets = Array.from(widgets.values())

    return markers[0]
}
